import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Percent } from 'lucide-react';
import { Button } from '../ui/button';
import { useCurrency } from '../../contexts/CurrencyContext';
import { calculateSalePrice } from '../../utils/calculateSalePrice';

export const FeaturedProducts = ({ products = [] }) => {
  const { currency, formatPrice, convertPrice } = useCurrency();
  const freezeDryers = products.filter(p => p.category === 'freeze-dryer');

  if (freezeDryers.length === 0) return null;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203]">
              Choose Your Size
            </h2>
            <p className="text-gray-600 mt-2">From compact to commercial, we have the perfect fit</p>
          </div>
          <Link to="/products">
            <Button variant="outline" className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white" data-testid="view-all-products-btn">
              View All <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
          {freezeDryers.map((product) => {
            const priceInfo = calculateSalePrice(product, currency);
            const displayPrice = priceInfo.isOnSale && priceInfo.effectivePrice ? priceInfo.effectivePrice : product.price;
            const monthlyPrice = Math.round(convertPrice(displayPrice) / 24);
            
            return (
              <Link key={product.id} to={`/products/${product.id}`} className="group" data-testid={`product-card-${product.id}`}>
                <div className="bg-[#f0f4f8] rounded-2xl overflow-hidden card-hover">
                  <div className="aspect-square bg-white overflow-hidden relative">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.size && (
                      <span className="absolute top-4 left-4 bg-[#0a0203] text-white px-3 py-1 rounded-full text-xs font-medium">
                        {product.size}
                      </span>
                    )}
                    {priceInfo.isOnSale && (
                      <span className="absolute top-4 right-4 bg-teal-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        {priceInfo.isCharmPricing ? (
                          <>SALE</>
                        ) : (
                          <><Percent className="h-3 w-3" />{priceInfo.savingsPercent}% OFF</>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0a0203] mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-1">{product.capacity || product.description?.substring(0, 50)}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                        {formatPrice(displayPrice)}
                      </span>
                      {priceInfo.isOnSale && (
                        <span className="text-sm text-gray-400 line-through">
                          {formatPrice(priceInfo.originalPrice)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">or {formatPrice(monthlyPrice)}/mo</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
